<template>
  <ChartCard
    v-if="!pageLoading"
    class="requisition-report-table"
    :data-widget-id="id"
  >
    <ChartHeader
      class="requisition-table-header"
      label="Applications Data Table"
    >
      <template #rightSide>
        <div class="d-flex align-center">
          <WarningWithModal
            v-if="table?.unmapped"
            text-key="req-report-warning-unmapped-cand"
          >
            <template #prepend:content>
              <h6 class="mb-2 subtitle">
                {{ table.unmapped }} candidate{{ table.unmapped === 1 ? ' is' : 's are' }} pending stage mapping
              </h6>
            </template>
            <template #action>
              <div class="action-container">
                <DpplAutoSizedButton
                  class="email-button"
                  :href="`mailto:insightsqa@datapeople.io?subject=${subject}&body=${body}`"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Request Assistance
                </DpplAutoSizedButton>
              </div>
            </template>
          </WarningWithModal>
          <RequisitionReportWidgetCommentsToggle
            class="mr-3"
            :widget-id="id"
            :req-id="reqId"
            :req-uuid="reqUuid"
            :screenshot-selector="`.requisition-report-table[data-widget-id='${id}']`"
            :thread-name="title"
          />
          <ChartDownload
            :download-id="id"
            :file-name="`${projectName}-requisition-report-${id}-${today}`"
            :class="{ 'mr-3': Boolean(tooltip?.html) }"
            include-csv-download-option
            :disabled="downloading"
            @download-csv="$emit('download-csv')"
          />
          <InsightsTooltip :tooltip="tooltip" />
        </div>
      </template>
    </ChartHeader>
    <div class="px-4 pb-4">
      <InsightsTable
        :rows="opened ? rows : []"
        :headers="headers"
        :totals="totals"
        show-passthrough-header
        total-row-clickable
        :highlight-cell="selectedStage ? `${highlightedHeaderText}:Active` : undefined"
        @total-row-click="opened = !opened"
      >
        <template #total:label>
          <span class="pa-2 new-h3" :class="{ fade: Boolean(selectedStage) }">
            Total
            <v-icon :icon="opened ? '$chevron-up' : '$chevron-down'" />
          </span>
        </template>
        <template
          v-for="option in candidateStageOptions"
          :key="option"
          #[`after-value:Active:${option}`]="{ item }"
        >
          <RequisitionReportCandidatesChip
            v-if="candidateStageOptions.includes(option)"
            class="h-100"
            :stage="option"
            :value="item[option].value"
            :is-selected="selectedStage === option"
            @set-candidate-stage="handleSetCandidateStage(option)"
          />
        </template>
      </InsightsTable>
    </div>
  </ChartCard>
  <TableSkeleton v-else class="loader" />
</template>

<script>
import { useRequisitionReport } from '@/stores/requisitions/requisitionReport';
import { mapState } from 'pinia';
import InsightsTooltip from '@/components/insights/InsightsTooltip.vue';
import { useAppLoading } from '@/stores/app/loading';
import TableSkeleton from '@/components/insights/skeletonLoaders/TableSkeleton.vue';
import { useTooltips } from '@/stores/insights/tooltips';
import WarningWithModal from '@/components/shared/WarningWithModal.vue';
import DpplAutoSizedButton from '@/components/datapeople/DpplAutoSizedButton.vue';
import { useAppAuthData } from '@/stores/app/authData';
import RequisitionReportWidgetCommentsToggle from '@/components/requisitions/RequisitionReportWidgetCommentsToggle.vue';
import RequisitionReportCandidatesChip from '@/components/requisitions/RequisitionReportCandidatesChip.vue';
import { useTracking } from '@/stores/tracking/tracking';
import ChartDownload from '@/components/insights/Chart/ChartDownload.vue';
import { format } from 'date-fns';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import InsightsTable from '@/components/insights/table/InsightsTable.vue';
import { getPassthroughRate } from '@/stores/insights/helpers';
import { READABLE_FILE_NAME_FORMAT } from '@/helpers/date';

export default {
  name: 'RequisitionReportTable',
  components: {
    InsightsTooltip,
    TableSkeleton,
    RequisitionReportWidgetCommentsToggle,
    WarningWithModal,
    DpplAutoSizedButton,
    RequisitionReportCandidatesChip,
    ChartDownload,
    ChartCard,
    ChartHeader,
    InsightsTable,
  },
  props: {
    selectedStage: { type: String, default: null },
    candidateStageOptions: { type: Array, default: () => [] },
    projectName: { type: String, default: '' },
    downloading: { type: Boolean, default: false },
  },
  emits: ['set-candidate-stage', 'download-csv'],
  data() {
    return {
      tooltipKey: 'single-req-table',
      id: 'applicants-table',
      opened: true,
      today: format(new Date(), READABLE_FILE_NAME_FORMAT),
      headers: [
        'Applied',
        'Screening',
        'Assessment',
        'Offer',
        'Hired',
      ],
    };
  },
  computed: {
    ...mapState(useTooltips, ['tooltips']),
    ...mapState(useRequisitionReport, ['table', 'reqId', 'reqUuid', 'title']),
    ...mapState(useAppLoading, ['pageLoading']),
    ...mapState(useAppAuthData, ['authData']),
    highlightedHeaderText() {
      return {
        application: 'Applied',
        screen: 'Screening',
        assessment: 'Assessment',
        offer: 'Offer',
      }[this.selectedStage] || '';
    },
    rows() {
      const stages = ['application', 'screen', 'assessment', 'offer'];
      const statusLabels = {
        active: 'Active',
        advanced: 'Advanced',
        rejected: 'Rejected',
        dropped_out: 'Dropped Out',
      };

      const tableRows = [];

      Object.entries(statusLabels).forEach(([statusKey, statusLabel]) => {
        const row = {
          label: statusLabel,
        };

        stages.forEach((stage) => {
          const stageData = (this.table && this.table[stage]) || {};
          row[stage] = {
            value: stageData[statusKey] || 0,
          };
        });

        row.hired = {
          value: '',
        };

        tableRows.push(row);
      });

      return tableRows;
    },
    totals() {
      const table = this.table || {};

      const totalApplied = Object.values(table.application || {}).reduce((a, b) => a + (b || 0), 0);
      const totalScreening = Object.values(table.screen || {}).reduce((a, b) => a + (b || 0), 0);
      const totalAssessment = Object.values(table.assessment || {})
        .reduce((a, b) => a + (b || 0), 0);
      const totalOffer = Object.values(table.offer || {}).reduce((a, b) => a + (b || 0), 0);
      const totalHired = table.hired || 0;

      const appliedToScreeningPassthroughRate = getPassthroughRate('applied', 'screening', {
        applied: totalApplied,
        screening: totalScreening,
      });
      const screeningToAssessmentPassthroughRate = getPassthroughRate('screening', 'assessment', {
        screening: totalScreening,
        assessment: totalAssessment,
      });
      const assessmentToOfferPassthroughRate = getPassthroughRate('assessment', 'offer', {
        assessment: totalAssessment,
        offer: totalOffer,
      });
      const offerToHiredPassthroughRate = getPassthroughRate('offer', 'hired', {
        offer: totalOffer,
        hired: totalHired,
      });

      return [
        {
          id: 'applied',
          value: totalApplied,
          percent: appliedToScreeningPassthroughRate,
          isPassthrough: true,
        },
        {
          id: 'screening',
          value: totalScreening,
          percent: screeningToAssessmentPassthroughRate,
          isPassthrough: true,
        },
        {
          id: 'assessment',
          value: totalAssessment,
          percent: assessmentToOfferPassthroughRate,
          isPassthrough: true,
        },
        {
          id: 'offer',
          value: totalOffer,
          percent: offerToHiredPassthroughRate,
          isPassthrough: true,
        },
        {
          id: 'hired',
          value: totalHired,
        },
      ];
    },
    subject() {
      const { authData } = this;
      const { companyName } = authData;

      return `JobNav Support: Candidate Mapping Inquiry for Requisition Progress Report - ${companyName}`;
    },
    body() {
      const { reqId, authData } = this;
      const {
        firstName, email, lastName,
      } = authData;
      const currentUrl = window.location.href;

      const lines = [
        'Hello,',
        '',
        `Can you please provide assistance with candidate mapping on requisition${reqId ? ` ${reqId}` : ''}?`,
        '',
        'You can view the report here:',
        currentUrl,
        '',
        'Thank you,',
        `${firstName} ${lastName || ''}`,
        email || '',
      ];

      return lines.join('%0A');
    },
    tooltip() {
      const { body_r: html, mode } = this.tooltips?.[this.tooltipKey] || {};

      return {
        html,
        mode,
        key: this.tooltipKey,
      };
    },
  },
  methods: {
    handleSetCandidateStage(stage) {
      if (!this.selectedStage && stage) {
        useTracking().trackName({
          name: 'Requisition Report: Opened Active Candidate Table',
        });
      }
      this.$emit('set-candidate-stage', stage);
    },
  },
};
</script>

<style lang="less" scoped>
@import '@/less/mixins';

.requisition-report-table {
  width: 100%;
  font-size: 14px;
  z-index: 2; // Prevents the candidate table from overlapping
}

.requisition-table-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.loader {
  height: 384px;
  border-radius: 8px;
  width: 100%;
  border: 1px solid @stroke-default;
}

:deep(.information-button) {
  margin-right: 12px;
}

.action-container {
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: flex-end;

  a.email-button {
    height: 32px !important;
    font-size: 12px !important;
  }
}

.subtitle {
  font-size: 14px;
}

.fade {
  opacity: 0.3;
}
</style>
